import * as _ from 'lodash';
import { NodeEventType, START_NODE } from '../diagram.model';
import { GatewayType, GatewayTypes, NodeTypes, TaskType, TaskTypes } from '../../../tamalelibs/models/workflow.model';
import { DiagramContextMenuEventType } from '../diagram-context-menu/diagram-context-menu.model';
import { BranchType, DiagramBranch, DiagramNode } from './diagram-node';
import { AutomationModelBase } from '../../../tamalelibs/models/automation-base.model';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';
export const BRANCH_PREFIX = 'con_';
export class NodeTemplate {
    name: string;
    children: Array<any>;
    guid: string;
    parents: Array<any>;
    type: any;
    diagramContextMenu: any;
    condition: any;
    branchs: Array<any>;
    active: boolean;
    valid: boolean;
    parentBranchID?: string;
}

export class DiagramNodesList {
    private nodesList: Map<string, DiagramNode>;
    private nodeTemplate: NodeTemplate = {
        name: '',
        children: [],
        guid: '',
        parents: [],
        type: TaskType.INPUT,
        diagramContextMenu: null,
        condition: '',
        branchs: [],
        active: true,
        valid: false,
    };

    roots: DiagramNode[];

    constructor(nodes: any[]) {
        this.nodesList = new Map();
        nodes.forEach(treeNode => {
            this.nodesList.set(
                treeNode.guid,
                new DiagramNode(treeNode, this.getThisNodeList.bind(this))
            );
        });
        this._makeRoots();
    }

    appendAutomation(automation: AutomationModelBase, id: string) {
        let nodeOrBranch: DiagramBranch | DiagramNode = this.getNode(id);
        if (!nodeOrBranch) {
            nodeOrBranch = this.getBranch(id);
            if (!nodeOrBranch) {
                return;
            }
        }
        if (!nodeOrBranch.automationConfigs) {
            nodeOrBranch.automationConfigs = [];
        }

        nodeOrBranch.automationConfigs.push(automation);
    }

    deleteNode(guid: string) {
        const target = this.getNode(guid);
        if (target.hasParents()) {
            target.parents.forEach(item => {
                const parent = this.getNode(item);
                if (parent) {
                    parent.children.delete(guid);
                    if (target.hasChildren()) {
                        target.children.forEach((child: string) => {
                            parent.children.add(child);
                        });
                    }
                }
            });
        }
        if (target.hasChildren()) {
            target.children.forEach((item) => {
                const child = this.getNode(item);
                child.condition = target.condition;
                child.parents.delete(guid);
                if (target.hasParents()) {
                    target.parents.forEach(parent => {
                        child.parents.add(parent);
                    });
                }
            });
        }
        this.nodesList.delete(guid);
        this._makeRoots();
        this.calculateNodeWrapWidth(target);
    }

    deleteBranch(nodeGuid, branchGuid, service) {
        const target = this.getNode(nodeGuid);
        const branchIndex = target.branchs.findIndex(item => item.guid === branchGuid);
        const deleteNodes = [];
        if (target.hasChildren()) {
            target.children.forEach(item => {
                const tempNode = this.getNode(item);
                // child nodes of branch only reflect on its condition.
                if (tempNode.condition === branchGuid) {
                    // mark the node whose conditionn is the abondoned branch as deleted.
                    deleteNodes.push(item);
                    // mark the child nodes of the found node as deleted.
                    this._getAllChildrenNode(deleteNodes, item);
                }
            });
        }
        // delete branch
        target.branchs.splice(branchIndex, 1);
        // delete all branch node
        deleteNodes.forEach(guid => {
            target.children.delete(guid);
            this.nodesList.delete(guid);
        });
        // if only has one branch, the node will change input type
        if (target.branchs.length < 2) {
            let branchChildNode;
            // update target node - child node, branch node
            if (target.hasChildren()) {
                // set branch node is parent node
                target.children.forEach(item => {
                    const tempNode = this.getNode(item);
                    if (tempNode.condition === target.branchs[0].guid) {
                        tempNode.condition = target.condition;
                        tempNode.parents.delete(target.guid);
                        target.parents.forEach(parent => {
                            tempNode.parents.add(parent);
                        });
                        branchChildNode = tempNode;
                    }
                });
                if (!branchChildNode) { // if the branch has not branch child, set other child parents
                    target.children.forEach(item => {
                        const tempNode = this.getNode(item);
                        tempNode.parents.delete(target.guid);
                        target.parents.forEach(parent => {
                            tempNode.parents.add(parent);
                        });
                    });
                } else {
                    // if the branch has branch child, set other child parents is lastest branch node
                    if (target.children.size !== 1) {
                        const newParentNode = this._getLastChildNode(branchChildNode.guid);
                        target.children.forEach(item => {
                            const tempNode = this.getNode(item);
                            if (tempNode.condition === DiagramNode.getNoneCondition() && item !== newParentNode.guid) {
                                tempNode.parents.delete(target.guid);
                                tempNode.parents.add(newParentNode.guid);
                                newParentNode.children.add(item);
                            }
                        });
                    }
                }
            }
            // update targer parent node
            if (target.hasParents()) {
                target.parents.forEach(item => {
                    const parent = this.getNode(item);
                    if (parent) {
                        parent.children.delete(nodeGuid);
                        if (branchChildNode) {
                            parent.children.add(branchChildNode.guid);
                        } else {
                            if (target.hasChildren()) {
                                target.children.forEach((child: string) => {
                                    parent.children.add(child);
                                });
                            }
                        }
                    }
                });
            }
            // delete the node directly
            this.nodesList.delete(nodeGuid);
            deleteNodes.push(nodeGuid);
        }
        service.diagramConfig.events$.next({
            type: NodeEventType.deleteNodes,
            payload: deleteNodes
        });
        this._makeRoots();
        this.calculateNodeWrapWidth(target);
    }

    destory() {
        const nodes = Array.from(this.values());
        nodes.forEach(node => {
            this.nodesList.delete(node.guid);
        });
        this._makeRoots();
    }

    getRootNote() {
        let node;
        this.nodesList.forEach(item => {
            if (item.parents.has(START_NODE.guid)) {
                node = item;
            }
        });
        return node;
    }

    getNodesByTypes<T extends TaskType | GatewayType>(types: T[]): DiagramNode[] {
        const nodes = [];
        this.nodesList.forEach((item, key) => {
            if (types.includes(<T>item.type)) {
                nodes.push(item);
            }
            const grandChildren = [];
            this._getAllChildrenNode(grandChildren, item.id);
            nodes.push(...grandChildren.filter(gc => types.includes(gc.type)));
        });

        return nodes;
    }

    getNode(guid: string): DiagramNode {
        // Due to taskDefinationMap use id ,but new node from nodeList use guid.
        this.nodesList.forEach((item, key) => {
            if (key !== guid && item.id === guid) {
                guid = key;
            }
        });
        return this.nodesList.get(guid);
    }

    getBranch(guid: string): DiagramBranch {
        const targetBranches = [];
        const findBranchFunc = (id: string, branches: DiagramBranch[], targetList: DiagramBranch[]) => {
            if (id && branches && branches.length > 0) {
                const matchedBranch = branches.find(b => b.id === id || b.guid === id);
                if (matchedBranch) {
                    targetList.push(matchedBranch);
                } else {
                    branches.forEach(branch => findBranchFunc(id, branch.branchs, targetList));
                }
            }
        };

        this.nodesList.forEach((item, key) => {
            if (item.branchs && item.branchs.length > 0) {
                findBranchFunc(guid, item.branchs, targetBranches);
            }
        });

        return targetBranches && targetBranches.length > 0 ? targetBranches[0] : null;
    }

    getThisNodeList() {
        return this;
    }

    /**
     * traverse the tree to determine the depth of the given node.
     * @param id the id of the given node.
     * @returns an object including the depth of the given node, a flag indicating whether the given node is on a gateway branch and the id of the gateway or the given node.
     */
    getNodeDepth(id: string): { depth: number, isOnBranch: boolean, nodeId: string } {
        const result = { depth: 0, isOnBranch: false, nodeId: null };
        const traverseToRootFunc = (childId: string, depthResult: { depth: number, isOnBranch: boolean, nodeId?: string }) => {
            const node = this.getNode(childId);
            if (node) {
                if (node.condition && !DiagramNode.isNonBranchCondition(node.condition)) {
                    // mark there's a gateway branch on this path.
                    depthResult.isOnBranch = true;
                }
                if (depthResult.isOnBranch && !depthResult.nodeId && NodeTypes.includes(<GatewayType>node.type)) {
                    // only record the first gateway id when a branch is found on the path.
                    depthResult.nodeId = node.id;
                }
                if (node.hasParents()) {
                    depthResult.depth++;
                    const parentId = Array.from(node.parents)[0];
                    if (parentId !== START_NODE.guid) {
                        traverseToRootFunc(parentId, depthResult);
                    }
                }
            }
        };

        traverseToRootFunc(id, result);

        if (!result.nodeId) {
            result.nodeId = id;
        }
        return result;
    }

    /**
     * try to find if there's a gateway parent of the given node.
     * @param id the given node id
     * @returns return the id of the gateway parent node; return null if no parent gateway found.
     *
     */
    getGatewayParent(id: string): string {
        const result = { gatewayParentId: null };
        const asendToGatewayParentFunc = (childId: string, output: { gatewayParentId: string }) => {
            const node = this.getNode(childId);
            if (node && node.hasParents()) {
                const parentId = Array.from(node.parents)[0];
                if (parentId !== START_NODE.guid) {
                    const parentNode = this.getNode(parentId);
                    if (parentNode) {
                        if (GatewayTypes.includes(parentNode.type)) {
                            // found a gateway parent node, stop here.
                            // for a new gateway without saving the parentId is a guid not the real id, so change to it.
                            output.gatewayParentId = parentNode.id;
                        } else {
                            // found a parent of other node types, proceed asending.
                            asendToGatewayParentFunc(parentId, result);
                        }
                    }
                }
            }
        };

        asendToGatewayParentFunc(id, result);
        return result.gatewayParentId;
    }

    sortNodes(nodes: { id: string, guid: string, name: string, parents: Array<string>, children: Array<string> }[]) {
        const firstNode = nodes.find(n => n.parents.length > 0 && n.parents[0] === START_NODE.guid);
        const indexChildrenFunc = (_parent: { children: Array<string> }, _seed: { index: number }, _nodeList: Array<{ id: string, guid: string, name: string, parents: Array<string>, children: Array<string> }>) => {
            if (_parent && _parent.children.length > 0) {
                const childrenWithGrandchildren = [];
                _parent.children.forEach(cId => {
                    const childNode = _nodeList.find(n => n.id === cId);
                    if (childNode) {
                        childNode['index'] = _seed.index++;
                        if (childNode.children && childNode.children.length > 0) {
                            childrenWithGrandchildren.push(childNode);
                        }
                    }
                });
                if (childrenWithGrandchildren.length > 0) {
                    childrenWithGrandchildren.forEach(cNode => indexChildrenFunc(cNode, _seed, _nodeList));
                }
            }
        };
        const indexSeed = { index: 0 };
        if (firstNode) {
            firstNode['index'] = indexSeed.index++;
            indexChildrenFunc(firstNode, indexSeed, nodes);
        }
        ArrayHelperService.sort<any>(nodes, 'index');
    }

    serialize() {
        const out = [];
        this.nodesList.forEach((node: DiagramNode) => {
            const branchs = [];
            if (node.branchs && node.branchs.length > 0) {
                node.branchs.forEach(item => {
                    const one: any = {
                        id: item.id,
                        guid: item.guid,
                        name: item.name,
                        type: item.type,
                        active: item.active,
                        condition: item.condition,
                        branchs: item.branchs,
                        parents: Array.from(item.parents || new Map()),
                        children: Array.from(item.children || new Map()),
                        showInput: item.showInput,
                    };
                    if (item.automationConfigs) {
                        one.automationConfigs = item.automationConfigs;
                    }
                    branchs.push(one);
                });
            }
            const json: any = {
                id: node.id,
                guid: node.guid,
                name: node.name,
                type: node.type,
                stage: node.stage,
                stageColor: node.stageColor,
                condition: node.condition,
                branchs: branchs,
                gatewayConfig: node.gatewayConfig,
                valid: node.valid,
                parents: Array.from(node.parents),
                children: Array.from(node.children),
                showInput: node.showInput
            };
            if (node.automationConfigs) {
                json.automation = node.automationConfigs;
            }
            out.push(json);
        });
        // 1. the last node must be a leaf node without any child node.
        const nodeCandidates = out.filter(n => !n.children || n.children.length === 0);
        let deepestOneOnBranch: { depth: number, isOnBranch: boolean, nodeId: string } = null, deepestOneNotOnBranch: { depth: number, isOnBranch: boolean, nodeId: string } = null;
        if (nodeCandidates.length > 0) {
            // candidates more than one node, compare the depth of each of them
            nodeCandidates.forEach(lN => {
                const depthOfLN = this.getNodeDepth(lN.id);

                if (depthOfLN.isOnBranch) {
                    if (!deepestOneOnBranch || (deepestOneOnBranch && deepestOneOnBranch.depth < depthOfLN.depth)) {
                        deepestOneOnBranch = depthOfLN;
                    }
                } else {
                    if (!deepestOneNotOnBranch || (deepestOneNotOnBranch && deepestOneNotOnBranch.depth < depthOfLN.depth)) {
                        deepestOneNotOnBranch = depthOfLN;
                    }
                }
            });

            // handle the case which the last node is the parallel/conditional gateway.
            // let isLastNode = false;
            // const gateways = out.filter(n => n.type === GatewayType.PARALLEL || n.type === GatewayType.CONDITIONAL);
            // for (const gateway of gateways) {
            //     if (gateway.branchs.length === gateway.children.length) {
            //         const isLastGateway = this._isLastGatewayNode(gateway);
            //         if (isLastGateway) {
            //             gateway.isLastNode = true;
            //             isLastNode = true;
            //             break;
            //         }
            //     }
            // }

            // handle the case which the last node is input/approve task.
            // if (!isLastNode) {
            const availableDeepNode = deepestOneNotOnBranch || deepestOneOnBranch;
            if (availableDeepNode && availableDeepNode.nodeId) {
                const lastNode = out.find(n => n.id === availableDeepNode.nodeId);
                if (lastNode) {
                    lastNode.isLastNode = true;
                }
            }
            // }
        }
        return out;
    }

    updateAutomation(automation: AutomationModelBase, id: string) {
        let node: DiagramNode | DiagramBranch = this.getNode(id);
        if (!node) {
            node = this.getBranch(id);
        }

        if (node && node.automationConfigs && node.automationConfigs.length > 0) {
            const config = node.automationConfigs.find(ac => ac.id === automation.id);
            AutomationModelBase.syncTo(automation, config);
        }
    }

    values() {
        return this.nodesList.values();
    }

    setNodeId(guid, id) {
        const target = this.getNode(guid);
        target.id = id;
    }

    updateNode(guid, data) {
        let target = this.getNode(guid);
        target = Object.assign(target, data);
    }

    moveAutomation(data) {
        // get the move automation node and target node
        const moveAutomationNode = data.moveAutomation;
        const targetNode = this.getNode(data.targetId);
        // get the original node
        const origninalNode = this.getNode(data.moveAutomation.attachedNodeId);
        // validation for moving node
        if (!moveAutomationNode || !targetNode) {
            return;
        }

        // remove the automation from the original node
        if (origninalNode) {
            const index = origninalNode.automationConfigs.indexOf(moveAutomationNode);
            origninalNode.automationConfigs.splice(index, 1);
            if (origninalNode.gatewayConfig) {
                origninalNode.gatewayConfig.forEach(item => {
                    if (item.automation) {
                        const aIndex = item.automation.indexOf(moveAutomationNode);
                        item.automation.splice(aIndex, 1);
                    }
                });
            }
        } else {
            const orignialBranch = this.getBranch(data.moveAutomation.attachedNodeId);
            const index = orignialBranch.automationConfigs.indexOf(moveAutomationNode);
            orignialBranch.automationConfigs.splice(index, 1);
        }
        // add the automation node to the target node
        if (targetNode.type === GatewayType.CONDITIONAL) {
            if (data.condition !== 'none') {
                const branch = targetNode.branchs.find(_branch => _branch.id === data.condition || _branch.guid === data.condition);
                if (branch) {
                    // change the attchedNodeId to avoid continuous changes
                    moveAutomationNode.attachedNodeId = branch.guid || branch.id;
                    if (branch.automationConfigs) {
                        branch.automationConfigs.push(moveAutomationNode);
                    } else {
                        branch['automationConfigs'] = [moveAutomationNode];
                    }
                } else {
                    // change the attchedNodeId to avoid continuous changes
                    moveAutomationNode.attachedNodeId = targetNode.id;
                    if (targetNode.automationConfigs) {
                        targetNode.automationConfigs.push(moveAutomationNode);
                    } else {
                        targetNode['automationConfigs'] = [moveAutomationNode];
                    }
                }
            } else {
                // change the attchedNodeId to avoid continuous changes
                moveAutomationNode.attachedNodeId = targetNode.id;
                if (targetNode.automationConfigs) {
                    targetNode.automationConfigs.push(moveAutomationNode);
                } else {
                    targetNode['automationConfigs'] = [moveAutomationNode];
                }
            }
        } else {
            // change the attchedNodeId to avoid continuous changes
            moveAutomationNode.attachedNodeId = targetNode.id;
            if (targetNode.automationConfigs) {
                targetNode.automationConfigs.push(moveAutomationNode);
            } else {
                targetNode['automationConfigs'] = [moveAutomationNode];
            }
            if (targetNode.type === GatewayType.PARALLEL && targetNode.gatewayConfig) {
                targetNode.gatewayConfig.forEach(gateway => {
                    if (gateway.type === GatewayType.PARALLEL) {
                        if (gateway.automation) {
                            gateway.automation.push(moveAutomationNode);
                        } else {
                            gateway.automation = [moveAutomationNode];
                        }
                    }
                });
            }
        }
        this._makeRoots();
    }

    moveNode(data) {
        // get the mode node and target node
        let moveNodeId = data.moveTask.guid; // move node id
        let moveNode = this.getNode(moveNodeId); // the node being moved.
        if (!moveNode) {
            moveNodeId = data.moveTask.id; // move node id
            moveNode = this.getNode(data.moveTask.id);
        }

        const targetNodeId = data.targetId; // target node id
        const targetNode = this.getNode(targetNodeId); // target node

        // validation for moving node
        if (!moveNode || moveNodeId === targetNodeId) {
            return;
        }

        // the parent and children of the node which is moved
        let childrenNodeOfMovingNodeId = '';
        let childrenNodeOfMovingNode;
        if (moveNode.children.size > 0) {
            childrenNodeOfMovingNodeId = Array.from(moveNode.children)[0];
            childrenNodeOfMovingNode = this.getNode(childrenNodeOfMovingNodeId);
        }
        let parentNodeOfMovingNodeId = '';
        let parentNodeOfMovingNode;
        parentNodeOfMovingNodeId = Array.from(moveNode.parents)[0];
        if (parentNodeOfMovingNodeId === START_NODE.guid) {
            const startNodeTemplate: NodeTemplate = Object.assign({}, this.nodeTemplate);
            startNodeTemplate.guid = START_NODE.guid;
            const startNode = new DiagramNode(startNodeTemplate, null);
            parentNodeOfMovingNode = startNode;
        } else {
            parentNodeOfMovingNode = this.getNode(parentNodeOfMovingNodeId);
        }

        if (parentNodeOfMovingNodeId === START_NODE.guid) {
            childrenNodeOfMovingNode.parents.clear();
            childrenNodeOfMovingNode.parents.add(START_NODE.guid);
        } else {
            if (GatewayTypes.indexOf(parentNodeOfMovingNode.type) > -1) {
                // parent node of moving mode is a gateway.
                parentNodeOfMovingNode.children.delete(moveNodeId);
                if (moveNode.children.size > 0) {
                    let _updated = false;
                    // iterate branches of target gateway to update the condition of each child of the node being moved.
                    parentNodeOfMovingNode.branchs.forEach((branch, index) => {
                        if (branch.guid.includes(BRANCH_PREFIX + moveNodeId) || (moveNode.condition && branch.guid.includes(moveNode.condition))) {
                            branch.guid = BRANCH_PREFIX + childrenNodeOfMovingNodeId + '_' + index;
                            childrenNodeOfMovingNode.condition = branch.guid;
                            _updated = true;
                        }
                    });

                    if (childrenNodeOfMovingNodeId) {
                        // connect the child node of moving node with the end node of the gateway,
                        // in other words, the child node is the first node right after the gateway node off any its branch.
                        parentNodeOfMovingNode.children.add(childrenNodeOfMovingNodeId);
                        if (!_updated) {
                            childrenNodeOfMovingNode.condition = DiagramNode.getNoneCondition();
                        }
                        childrenNodeOfMovingNode.parents.clear();
                        childrenNodeOfMovingNode.parents.add(parentNodeOfMovingNodeId);
                    }
                } else {
                    const index = parentNodeOfMovingNode.branchs.findIndex(item => item.guid.includes(moveNodeId));
                    if (index > -1) {
                        parentNodeOfMovingNode.branchs[index].guid = 'con_' + this._uuidv4() + '_' + index;
                    } else {
                        if (childrenNodeOfMovingNodeId) {
                            parentNodeOfMovingNode.children.add(childrenNodeOfMovingNodeId);

                            if (parentNodeOfMovingNodeId) {
                                childrenNodeOfMovingNode.parents.clear();
                                childrenNodeOfMovingNode.parents.add(parentNodeOfMovingNodeId);
                                childrenNodeOfMovingNode.condition = DiagramNode.getNoneCondition();
                            }
                        }
                    }
                }
                moveNode.condition = '';
            } else {
                parentNodeOfMovingNode.children.clear();
                if (childrenNodeOfMovingNodeId) {
                    parentNodeOfMovingNode.children.add(childrenNodeOfMovingNodeId);
                }
                if (childrenNodeOfMovingNode) {
                    childrenNodeOfMovingNode.parents.clear();
                    childrenNodeOfMovingNode.parents.add(parentNodeOfMovingNodeId);
                }
            }
        }

        // the parent and children of target node
        let updatedNodes = [];
        if (targetNodeId === START_NODE.guid) {
            this.nodesList.forEach(item => {
                if (item.parents.has(START_NODE.guid)) {
                    updatedNodes.push(item.guid);
                }
            });
            moveNode.children.clear();
            updatedNodes.forEach(id => {
                const node = this.getNode(id);
                node.parents.clear();
                node.parents.add(moveNodeId);
                moveNode.children.add(id);
            });
            moveNode.parents.clear();
            moveNode.parents.add(START_NODE.guid);
        } else {
            if (TaskTypes.indexOf(targetNode.type) > -1) {
                // handle moving node
                moveNode.children.clear();
                if (targetNode) {
                    if (targetNode.children.size > 0) {
                        moveNode.children.add(Array.from(targetNode.children)[0]);
                    }
                }
                moveNode.parents.clear();
                moveNode.parents.add(targetNodeId);

                updatedNodes = Array.from(targetNode.children);
                updatedNodes.forEach(item => {
                    const tempNode = this.getNode(item);
                    tempNode.parents.clear();
                    tempNode.parents.add(moveNodeId);
                });
                targetNode.children.clear();
                targetNode.children.add(moveNodeId);
            } else if (GatewayTypes.indexOf(targetNode.type) > -1) {
                // target node is a gateway
                moveNode.children.clear();
                moveNode.parents.clear();
                moveNode.parents.add(targetNodeId);

                targetNode.children.add(moveNodeId);
                if (data.condition !== DiagramNode.getNoneCondition()) {
                    // move the node to a branch of the target gateway.
                    targetNode.branchs.forEach((branch, index) => {
                        if (branch.guid === data.condition) {
                            // found the target branch.
                            branch.guid = 'con_' + moveNodeId + '_' + index;
                            // update the condition of the node being moved as the target branch.
                            moveNode.condition = branch.guid;
                            let childId = '';
                            targetNode.children.forEach(nodeId => {
                                const node = this.getNode(nodeId);
                                // update the parent of each existing node on the target branch as the node being moved
                                // and empty its condition to disconnect with the branch.
                                // And then mark them as the child nodes of the node bening moved.
                                if (node.condition === data.condition) {
                                    node.parents.clear();
                                    node.parents.add(moveNodeId);
                                    node.condition = '';
                                    moveNode.children.add(nodeId);
                                    childId = nodeId;
                                }
                            });

                            if (childId) {
                                targetNode.children.delete(childId);
                            }
                        }
                    });
                } else {
                    let childrenNodeOfTargetNode;
                    targetNode.children.forEach(id => {
                        const node = this.getNode(id);
                        if (node.condition === DiagramNode.getNoneCondition()) {
                            childrenNodeOfTargetNode = node;
                        }
                    });
                    if (childrenNodeOfTargetNode) {
                        targetNode.children.delete(childrenNodeOfTargetNode.guid);
                        childrenNodeOfTargetNode.parents.clear();
                        childrenNodeOfTargetNode.parents.add(moveNodeId);
                        childrenNodeOfTargetNode.condition = '';

                        moveNode.children.add(childrenNodeOfTargetNode.guid);
                    }
                    moveNode.condition = DiagramNode.getNoneCondition();
                }
            }
        }

        this._makeRoots();
    }

    newNode(data, service) {
        const newNodeTemplate: NodeTemplate = Object.assign({}, this.nodeTemplate);
        newNodeTemplate.guid = this._uuidv4();
        newNodeTemplate.type = data.type;
        if (TaskTypes.includes(newNodeTemplate.type)) {
            // default task nodes as invalid, so that push user to populate the required attributes to get task nodes valid.
            newNodeTemplate.valid = false;
        } else {
            newNodeTemplate.valid = true;
        }

        newNodeTemplate.branchs = [];
        if (data.condition) {
            newNodeTemplate.condition = data.condition;
        }
        if (data.parentBranch) {
            newNodeTemplate.parentBranchID = data.parentBranch;
        }
        newNodeTemplate.diagramContextMenu = data.diagramContextMenu;
        this.nodesList.set(
            newNodeTemplate.guid,
            new DiagramNode(
                newNodeTemplate,
                this.getThisNodeList.bind(this)
            )
        );
        const newNode = this.getNode(newNodeTemplate.guid);
        // target node is the one new node need to connect with.
        const targertNodeId = data.targetId;
        let effectNotes = [];
        if (targertNodeId === START_NODE.guid) {
            // add a node and connect with the starting point.
            this.nodesList.forEach(item => {
                if (item.parents.has(START_NODE.guid)) {
                    effectNotes.push(item.guid);
                }
            });
            const availableGatewayTypes: Array<GatewayType | TaskType> = [GatewayType.PARALLEL, GatewayType.CONDITIONAL];
            if (availableGatewayTypes.includes(newNode.type)) {
                effectNotes.forEach(item => {
                    const tempNode = this.getNode(item);
                    tempNode.condition = DiagramNode.getNoneCondition();
                });
            }
        } else {
            // add a new node and connect with a non-start node.
            const targetNode = this.getNode(targertNodeId);
            if (TaskTypes.indexOf(targetNode.type) > -1) {
                // connect to a task node rather than a gateway node.
                effectNotes = Array.from(targetNode.children);
                effectNotes.forEach(item => {
                    const tempNode = this.getNode(item);
                    tempNode.condition = DiagramNode.getNoneCondition();
                });
                targetNode.children.clear();
            } else if (GatewayTypes.indexOf(targetNode.type) > -1) {
                // connect to a gateway node.
                targetNode.children.forEach(item => {
                    const tempNode = this.getNode(item);
                    if (tempNode.condition === newNode.condition) {
                        tempNode.condition = DiagramNode.getNoneCondition();
                        effectNotes.push(item);
                    }
                });
                effectNotes.forEach(item => {
                    targetNode.children.delete(item);
                });
            }
            // connect to the target node as a child node.
            targetNode.children.add(newNodeTemplate.guid);
        }
        // hold the parent node id reversely
        newNode.parents.add(targertNodeId);
        // take the target's childern as the child nodes of the new node.
        effectNotes.forEach(element => {
            newNode.children.add(element);
            const tempNode = this.getNode(element);
            tempNode.parents.delete(targertNodeId);
            tempNode.parents.add(newNodeTemplate.guid);
        });
        if (GatewayTypes.indexOf(data.type) > -1) {
            // create two branch node templates for the new node and add them to the branches of the new node.
            const branch_1 = this.newBranchNode(newNode);
            newNode.branchs.push(branch_1);

            const branch_2 = this.newBranchNode(newNode);
            newNode.branchs.push(branch_2);
        }
        this.nodesList.forEach(item => {
            item.active = item.guid === newNodeTemplate.guid;
        });
        this._makeRoots();
        this.calculateNodeWrapWidth(newNode);
        newNode.addNodeEventEmit(service, newNode);
    }

    calculateNodeWrapWidth(node) {
        if (node.diagramContextMenu) {
            node.diagramContextMenu.events$.next({
                type: DiagramContextMenuEventType.resetWidth
            });
        }
    }

    newBranchNode(node) {
        const newNodeTemplate = Object.assign({}, this.nodeTemplate);
        newNodeTemplate.guid = this._uuidv4();
        newNodeTemplate.type = node.type === GatewayType.CONDITIONAL ? BranchType.ConditionalBranch : BranchType.NormalBranch;
        newNodeTemplate.diagramContextMenu = node.diagramContextMenu;
        newNodeTemplate.parents = node.parents;
        newNodeTemplate.branchs = [];
        newNodeTemplate.active = true;
        if (node.type === GatewayType.CONDITIONAL) {
            // default the condition of the conditional branch to true.
            newNodeTemplate.condition = true;
        }
        node.branchs.forEach(item => {
            item.active = item.guid === newNodeTemplate.guid;
        });
        return newNodeTemplate;
    }

    private _uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            // tslint:disable-next-line:one-variable-per-declaration no-bitwise
            const r = (Math.random() * 16) | 0;
            // tslint:disable-next-line:triple-equals no-bitwise
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    private _makeRoots() { // refresh diagram page
        this.roots = Array.from(this.values()).filter((node: DiagramNode) =>
            node.isRoot()
        );
    }

    private _getAllChildrenNode(children = [], guid) {
        const node = this.getNode(guid);
        if (node.hasChildren()) {
            node.children.forEach(item => {
                children.push(item);
                this._getAllChildrenNode(children, item);
            });
        }
        return children;
    }

    private _getLastChildNode(guid, tempNode = null) {
        const node = this.getNode(guid);
        if (node.hasChildren()) {
            node.children.forEach(item => {
                const child = this.getNode(item);
                if (!child.condition || child.condition === DiagramNode.getNoneCondition()) {
                    tempNode = this._getLastChildNode(item, tempNode);
                } else {
                    tempNode = node;
                }
            });
        } else {
            tempNode = node;
        }
        return tempNode;
    }

    private _isLastGatewayNode(node) {
        if (node.parents[0] === START_NODE.guid ||
            Array.from(node.parents.values())[0] === START_NODE.guid) {
            if (node.type === GatewayType.PARALLEL || node.type === GatewayType.CONDITIONAL) {
                if (node.branchs && node.children) {
                    return true;
                }
            } else {
                return true;
            }
        } else {
            const parentId = node.parents[0] || Array.from(node.parents.values())[0];
            const parentNode = this.getNode(parentId);
            if (parentNode.type === GatewayType.PARALLEL || parentNode.type === GatewayType.CONDITIONAL) {
                if (parentNode.children.size > parentNode.branchs.length) {
                    return this._isLastGatewayNode(parentNode);
                } else {
                    return false;
                }
            } else {
                return this._isLastGatewayNode(parentNode);
            }
        }
    }
}
